/**
 * Lot 3 — Gating partenaires.
 *
 * Lit users/{uid}.partnerSubscription en temps réel et indique si :
 *  - la base partenaire est active (`hasBase`)
 *  - l'add-on requis pour la route est actif (`hasAddon`)
 *
 * Utilisé par `InternalPartnerLayout` pour rediriger vers `/partner/billing`
 * si l'utilisateur n'a pas souscrit à l'add-on correspondant.
 *
 * Mapping URL → slug Stripe :
 *   business-law    → business_law
 *   immigration-law → immigration_law
 *   notary          → notary
 *   tax-advisor     → tax_advisor
 *   cpa             → cpa
 */
import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import {
  PARTNER_ADDON_SLUGS,
  type PartnerAddonSlug,
  type PartnerSubscriptionDoc,
} from "@/types/firestore/partner";

export function partnerTypeToAddonSlug(
  partnerType: string | undefined
): PartnerAddonSlug | null {
  if (!partnerType) return null;
  const normalized = partnerType.replace(/-/g, "_") as PartnerAddonSlug;
  return PARTNER_ADDON_SLUGS.includes(normalized) ? normalized : null;
}

export interface RequirePartnerAddonState {
  loading: boolean;
  hasBase: boolean;
  hasAddon: boolean;
  /** L'add-on attendu pour la route (null si la route n'est pas mappée). */
  requiredAddon: PartnerAddonSlug | null;
  /** Bypass admin/staff. */
  bypass: boolean;
  subscription: PartnerSubscriptionDoc | null;
}

const ADMIN_ROLES = new Set(["admin", "super_admin", "staff"]);

export function useRequirePartnerAddon(
  partnerType: string | undefined
): RequirePartnerAddonState {
  const { user, loading: authLoading } = useAuth();
  const required = partnerTypeToAddonSlug(partnerType);
  const bypass = !!user?.roles?.some((r) => ADMIN_ROLES.has(r));

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<PartnerSubscriptionDoc | null>(
    null
  );

  useEffect(() => {
    if (authLoading) return;
    if (!user?.id) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ref = doc(db, "users", user.id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as
          | { partnerSubscription?: PartnerSubscriptionDoc }
          | undefined;
        setSubscription(data?.partnerSubscription ?? null);
        setLoading(false);
      },
      () => {
        setSubscription(null);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [authLoading, user?.id]);

  const status = subscription?.status;
  const isActiveStatus = status === "active" || status === "trialing";
  const hasBase = !!subscription?.baseActive && isActiveStatus;
  const hasAddon =
    bypass ||
    (required !== null &&
      hasBase &&
      Array.isArray(subscription?.addons) &&
      subscription!.addons.includes(required));

  return {
    loading: authLoading || loading,
    hasBase: bypass || hasBase,
    hasAddon,
    requiredAddon: required,
    bypass,
    subscription,
  };
}
